/**
 * Soul Onboarding
 *
 * A 5-step conversational flow that captures the user's persona, priorities,
 * scope, current challenge, and information preferences.
 * Designed to match the Command Center light theme.
 */

import { useState } from "react";
import { Button, Input, message } from "antd";
import {
	ArrowLeftOutlined,
	ArrowRightOutlined,
	CheckOutlined,
	UserOutlined,
	AimOutlined,
	GlobalOutlined,
	ThunderboltOutlined,
	EyeOutlined,
	ToolOutlined,
	RiseOutlined,
	TeamOutlined,
	DollarOutlined,
	BarChartOutlined,
	SafetyCertificateOutlined,
	BulbOutlined,
	HeartOutlined,
	AlertOutlined,
	FileTextOutlined,
	PieChartOutlined,
	BellOutlined,
} from "@ant-design/icons";
import type {
	PersonaType,
	PriorityType,
	ScopeType,
	InfoStyleType,
	CreateSoulPayload,
} from "@/services/soul-service";
import { createUserSoul } from "@/services/soul-service";
import { useSoulStore } from "@/stores/soul-store";
import "./SoulOnboarding.scss";

interface SoulOnboardingProps {
	userName: string;
	onComplete: () => void;
}

// ─── Step Data ───

const PERSONAS: { key: PersonaType; label: string; description: string; icon: React.ReactNode }[] = [
	{ key: "facility_manager", label: "Facility Manager", description: "I manage buildings and equipment day-to-day", icon: <ToolOutlined /> },
	{ key: "executive", label: "Executive", description: "I need high-level oversight and KPIs", icon: <RiseOutlined /> },
	{ key: "general_manager", label: "General Manager", description: "I oversee operations at one or more locations", icon: <TeamOutlined /> },
	{ key: "location_worker", label: "Location Worker", description: "I work on-site and interact with equipment directly", icon: <UserOutlined /> },
	{ key: "finance", label: "Finance", description: "I focus on budgets, costs, and ROI", icon: <DollarOutlined /> },
	{ key: "analyst", label: "Analyst", description: "I dig into data, trends, and reports", icon: <BarChartOutlined /> },
	{ key: "other", label: "Other", description: "Something else entirely", icon: <BulbOutlined /> },
];

const PRIORITIES: { key: PriorityType; label: string; icon: React.ReactNode }[] = [
	{ key: "uptime", label: "Equipment Uptime", icon: <ThunderboltOutlined /> },
	{ key: "cost_control", label: "Cost Control", icon: <DollarOutlined /> },
	{ key: "compliance", label: "Compliance & Regulations", icon: <SafetyCertificateOutlined /> },
	{ key: "safety", label: "Safety", icon: <AlertOutlined /> },
	{ key: "energy_efficiency", label: "Energy Efficiency", icon: <BulbOutlined /> },
	{ key: "tenant_satisfaction", label: "Tenant / Customer Satisfaction", icon: <HeartOutlined /> },
];

const SCOPES: { key: ScopeType; label: string; description: string }[] = [
	{ key: "single_location", label: "A single location", description: "I focus on one site" },
	{ key: "few_locations", label: "A few locations", description: "2-10 sites in my area" },
	{ key: "regional", label: "A region", description: "Multiple sites across a region" },
	{ key: "national", label: "Nationwide", description: "Sites across the country" },
	{ key: "corporate_all", label: "The entire organization", description: "All sites, all operations" },
];

const INFO_STYLES: { key: InfoStyleType; label: string; description: string; icon: React.ReactNode }[] = [
	{ key: "quick_summaries", label: "Quick Summaries", description: "Give me the highlights fast", icon: <AimOutlined /> },
	{ key: "detailed_reports", label: "Detailed Reports", description: "I want the full picture", icon: <FileTextOutlined /> },
	{ key: "visual_charts", label: "Visual Charts & Graphs", description: "Show me the data visually", icon: <PieChartOutlined /> },
	{ key: "just_alerts", label: "Just Alerts", description: "Only tell me what needs attention", icon: <BellOutlined /> },
];

const TOTAL_STEPS = 5;

const SoulOnboarding = ({ userName, onComplete }: SoulOnboardingProps) => {
	const { setSoul } = useSoulStore();
	const [step, setStep] = useState(0);
	const [isSaving, setIsSaving] = useState(false);

	// Form state
	const [persona, setPersona] = useState<PersonaType | null>(null);
	const [personaOther, setPersonaOther] = useState("");
	const [priorities, setPriorities] = useState<PriorityType[]>([]);
	const [scope, setScope] = useState<ScopeType | null>(null);
	const [currentChallenge, setCurrentChallenge] = useState("");
	const [infoStyle, setInfoStyle] = useState<InfoStyleType | null>(null);

	const canGoNext = (): boolean => {
		switch (step) {
			case 0: return persona !== null && (persona !== "other" || personaOther.trim().length > 0);
			case 1: return priorities.length > 0;
			case 2: return scope !== null;
			case 3: return true; // Challenge is optional
			case 4: return infoStyle !== null;
			default: return false;
		}
	};

	const togglePriority = (key: PriorityType) => {
		setPriorities(prev => {
			if (prev.includes(key)) return prev.filter(p => p !== key);
			if (prev.length >= 3) return prev; // Max 3
			return [...prev, key];
		});
	};

	const handleNext = () => {
		if (step < TOTAL_STEPS - 1) {
			setStep(step + 1);
		} else {
			handleSave();
		}
	};

	const handleBack = () => {
		if (step > 0) setStep(step - 1);
	};

	const handleSkip = () => {
		// Skip sets reasonable defaults and saves
		const payload: CreateSoulPayload = {
			persona: persona || "facility_manager",
			personaOther: personaOther || undefined,
			priorities: priorities.length > 0 ? priorities : ["uptime"],
			scope: scope || undefined,
			currentChallenge: currentChallenge || undefined,
			infoStyle: infoStyle || "quick_summaries",
		};
		saveSoul(payload);
	};

	const handleSave = () => {
		if (!persona) return;
		const payload: CreateSoulPayload = {
			persona,
			personaOther: persona === "other" ? personaOther : undefined,
			priorities,
			scope: scope || undefined,
			currentChallenge: currentChallenge || undefined,
			infoStyle: infoStyle || undefined,
		};
		saveSoul(payload);
	};

	const saveSoul = async (payload: CreateSoulPayload) => {
		setIsSaving(true);
		try {
			const soul = await createUserSoul(payload);
			if (soul) {
				setSoul(soul);
				message.success("Profile created! Your experience is now personalized.");
			}
			onComplete();
		} catch (error) {
			message.error("Failed to save profile. Please try again.");
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<div className="soul-onboarding">
			<div className="soul-card">
				{/* Progress dots */}
				<div className="soul-progress">
					{Array.from({ length: TOTAL_STEPS }).map((_, i) => (
						<div
							key={i}
							className={`soul-dot ${i === step ? "active" : ""} ${i < step ? "completed" : ""}`}
						/>
					))}
				</div>

				{/* Step 0: Persona */}
				{step === 0 && (
					<div className="soul-step">
						<h2 className="soul-title">Welcome, {userName}!</h2>
						<p className="soul-subtitle">Let's personalize your experience. Who are you?</p>
						<div className="soul-persona-grid">
							{PERSONAS.map((p) => (
								<button
									key={p.key}
									type="button"
									className={`soul-persona-card ${persona === p.key ? "selected" : ""}`}
									onClick={() => setPersona(p.key)}
								>
									<span className="soul-persona-icon">{p.icon}</span>
									<span className="soul-persona-label">{p.label}</span>
									<span className="soul-persona-desc">{p.description}</span>
								</button>
							))}
						</div>
						{persona === "other" && (
							<Input
								className="soul-other-input"
								placeholder="Tell us your role..."
								value={personaOther}
								onChange={(e) => setPersonaOther(e.target.value)}
								maxLength={100}
							/>
						)}
					</div>
				)}

				{/* Step 1: Priorities */}
				{step === 1 && (
					<div className="soul-step">
						<h2 className="soul-title">What matters most to you?</h2>
						<p className="soul-subtitle">Select up to 3 priorities that drive your daily work.</p>
						<div className="soul-priority-grid">
							{PRIORITIES.map((p) => (
								<button
									key={p.key}
									type="button"
									className={`soul-priority-chip ${priorities.includes(p.key) ? "selected" : ""}`}
									onClick={() => togglePriority(p.key)}
								>
									<span className="soul-priority-icon">{p.icon}</span>
									<span>{p.label}</span>
									{priorities.includes(p.key) && <CheckOutlined className="soul-check" />}
								</button>
							))}
						</div>
						<p className="soul-hint">{priorities.length}/3 selected</p>
					</div>
				)}

				{/* Step 2: Scope */}
				{step === 2 && (
					<div className="soul-step">
						<h2 className="soul-title">How much do you oversee?</h2>
						<p className="soul-subtitle">This helps us tailor the level of detail you see.</p>
						<div className="soul-scope-list">
							{SCOPES.map((s) => (
								<button
									key={s.key}
									type="button"
									className={`soul-scope-card ${scope === s.key ? "selected" : ""}`}
									onClick={() => setScope(s.key)}
								>
									<GlobalOutlined className="soul-scope-icon" />
									<div className="soul-scope-text">
										<span className="soul-scope-label">{s.label}</span>
										<span className="soul-scope-desc">{s.description}</span>
									</div>
									{scope === s.key && <CheckOutlined className="soul-check" />}
								</button>
							))}
						</div>
					</div>
				)}

				{/* Step 3: Challenge */}
				{step === 3 && (
					<div className="soul-step">
						<h2 className="soul-title">What's your biggest challenge right now?</h2>
						<p className="soul-subtitle">This helps the AI focus on what you need most. You can skip this.</p>
						<Input.TextArea
							className="soul-challenge-input"
							placeholder="e.g., Aging equipment at multiple locations, budget constraints, staying on top of maintenance schedules..."
							value={currentChallenge}
							onChange={(e) => setCurrentChallenge(e.target.value)}
							maxLength={500}
							rows={4}
							autoSize={{ minRows: 3, maxRows: 6 }}
						/>
					</div>
				)}

				{/* Step 4: Info Style */}
				{step === 4 && (
					<div className="soul-step">
						<h2 className="soul-title">How do you like your information?</h2>
						<p className="soul-subtitle">We'll tailor responses and reports to your preference.</p>
						<div className="soul-style-grid">
							{INFO_STYLES.map((s) => (
								<button
									key={s.key}
									type="button"
									className={`soul-style-card ${infoStyle === s.key ? "selected" : ""}`}
									onClick={() => setInfoStyle(s.key)}
								>
									<span className="soul-style-icon">{s.icon}</span>
									<span className="soul-style-label">{s.label}</span>
									<span className="soul-style-desc">{s.description}</span>
								</button>
							))}
						</div>
					</div>
				)}

				{/* Navigation */}
				<div className="soul-nav">
					<div className="soul-nav-left">
						{step > 0 && (
							<Button
								type="text"
								icon={<ArrowLeftOutlined />}
								onClick={handleBack}
								className="soul-back-btn"
							>
								Back
							</Button>
						)}
					</div>
					<div className="soul-nav-right">
						<Button
							type="text"
							onClick={handleSkip}
							className="soul-skip-btn"
						>
							Skip for now
						</Button>
						<Button
							type="primary"
							onClick={handleNext}
							disabled={!canGoNext()}
							loading={isSaving}
							className="soul-next-btn"
							icon={step === TOTAL_STEPS - 1 ? <CheckOutlined /> : <ArrowRightOutlined />}
						>
							{step === TOTAL_STEPS - 1 ? "Finish" : "Next"}
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

export { SoulOnboarding };
